import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, roleConfig } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Plus, Loader2, User, Mail, Shield, Trash2, Send
} from 'lucide-react';
import { toast } from 'sonner';

export default function UsersPage() {
  const { user, isAdmin, isJefatura } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const canManageUsers = isAdmin || isJefatura;

  const [form, setForm] = useState({
    email: '', password: '', first_name: '', last_name: '',
    role: 'solicitante', company_id: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const payload = { ...form, company_id: user.company_id };
      await axios.post(`${API_URL}/users`, payload);
      toast.success('Usuario creado exitosamente');
      setDialogOpen(false);
      setForm({ email: '', password: '', first_name: '', last_name: '', role: 'solicitante', company_id: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Estas seguro de eliminar este usuario?')) return;
    try {
      await axios.delete(`${API_URL}/users/${userId}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.patch(`${API_URL}/users/${userId}`, { role: newRole });
      toast.success('Rol actualizado');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar rol');
    }
  };

  // Roles disponibles segun el rol del usuario actual
  const availableRoles = isAdmin
    ? [
        { value: 'solicitante', label: 'Solicitante' },
        { value: 'solucionador', label: 'Solucionador' },
        { value: 'jefatura', label: 'Jefatura' },
        { value: 'admin', label: 'Administrador' },
      ]
    : [
        { value: 'solicitante', label: 'Solicitante' },
        { value: 'solucionador', label: 'Solucionador' },
        { value: 'jefatura', label: 'Jefatura' },
      ];

  // Puede editar el rol de un usuario?
  const canEditRole = (u) => {
    if (u.id === user.id) return false;
    if (isAdmin) return true;
    // Jefatura puede editar roles excepto admin
    if (user.role === 'jefatura' && u.role !== 'admin') return true;
    return false;
  };

  // Puede eliminar un usuario?
  const canDeleteUser = (u) => {
    if (u.id === user.id) return false;
    if (isAdmin) return true;
    if (user.role === 'jefatura' && u.role !== 'admin') return true;
    return false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#7FD02E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-gray-800">Usuarios</h1>
          <p className="text-gray-500">{users.length} usuarios en tu empresa</p>
        </div>
        {canManageUsers && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#7FD02E] hover:bg-[#6BC024] text-white shadow-sm" data-testid="new-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Usuario
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <form onSubmit={handleCreateUser}>
                <DialogHeader>
                  <DialogTitle className="text-gray-800">Crear Nuevo Usuario</DialogTitle>
                  <DialogDescription className="text-gray-500">
                    Agrega un nuevo usuario a tu empresa
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name" className="text-gray-700">Nombre</Label>
                      <Input
                        id="first_name"
                        value={form.first_name}
                        onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                        required
                        className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                        data-testid="user-firstname-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name" className="text-gray-700">Apellido</Label>
                      <Input
                        id="last_name"
                        value={form.last_name}
                        onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                        required
                        className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                        data-testid="user-lastname-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-700">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                      className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                      data-testid="user-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-gray-700">Contrasena</Label>
                    <Input
                      id="password"
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      required
                      minLength={6}
                      className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                      data-testid="user-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Rol</Label>
                    <Select value={form.role} onValueChange={(value) => setForm({ ...form, role: value })}>
                      <SelectTrigger className="border-gray-300" data-testid="user-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableRoles.map(r => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" className="text-gray-600" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={actionLoading}
                    className="bg-[#7FD02E] hover:bg-[#6BC024] text-white"
                    data-testid="submit-user-btn"
                  >
                    {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Crear Usuario
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Users Table */}
      <Card className="border-gray-200 bg-white shadow-sm" data-testid="users-table">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-100 hover:bg-transparent">
                <TableHead className="text-gray-500">Usuario</TableHead>
                <TableHead className="text-gray-500">Email</TableHead>
                <TableHead className="text-gray-500">Rol</TableHead>
                <TableHead className="text-gray-500">Telegram</TableHead>
                <TableHead className="text-gray-500">Estado</TableHead>
                {canManageUsers && <TableHead className="text-gray-500 text-right">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="border-gray-100" data-testid={`user-row-${u.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#7FD02E]/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-[#7FD02E]" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{u.first_name} {u.last_name}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-gray-500">
                      <Mail className="w-4 h-4" />
                      {u.email}
                    </div>
                  </TableCell>
                  <TableCell>
                    {canEditRole(u) ? (
                      <Select
                        value={u.role}
                        onValueChange={(value) => handleRoleChange(u.id, value)}
                      >
                        <SelectTrigger className="w-40 border-gray-300" data-testid={`role-select-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableRoles.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={roleConfig[u.role]?.color}>
                        <Shield className="w-3 h-3 mr-1" />
                        {roleConfig[u.role]?.label}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {u.telegram_chat_id ? (
                      <div className="flex items-center gap-2" data-testid={`telegram-${u.id}`}>
                        <Send className="w-3.5 h-3.5 text-[#229ED9]" />
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 font-mono text-xs">
                          {u.telegram_chat_id}
                        </Badge>
                      </div>
                    ) : (
                      <Badge variant="outline" className="text-gray-400 border-gray-200 text-xs" data-testid={`telegram-${u.id}-none`}>
                        Sin vincular
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}>
                      {u.is_active ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </TableCell>
                  {canManageUsers && (
                    <TableCell className="text-right">
                      {canDeleteUser(u) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteUser(u.id)}
                          className="text-gray-400 hover:text-destructive"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
